/* bambusa-devis-print.js — mise en page du devis imprimable (Bambusa)
   ------------------------------------------------------------------
   Fichier volontairement séparé de bambusa-espace.js : la mise en page
   d'un devis s'ajuste souvent, le moteur non. bambusa-espace.js appelle
   window.BE_PRINT(devis, totaux, conf, validiteJours) s'il existe.

   Le nom du fichier PDF proposé par le navigateur vient de document.title :
   on le force le temps de l'impression, puis on le restaure.

   🚨 LOGO — 4 garde-fous (04/08/2026). Cause du bug « logo absent une
   fois sur deux » : print() partait AVANT la fin du chargement de l'image,
   et l'URL venait de shop.brand.logo, pas toujours renseigné.
   1. L'URL du logo est ÉCRITE EN DUR ici (LOGO_URL, fichier permanent de
      la bibliothèque Shopify) : plus de dépendance à shop.brand.logo.
      conf.entreprise.logo (réglage `logo_devis`) reste prioritaire.
   2. On n'appelle window.print() qu'APRÈS le décodage effectif des images
      (attendreImages), jamais avant.
   3. Le logo est préchargé au chargement de la page ET mémorisé en
      data:URI dans localStorage → dès la 2ᵉ visite, zéro requête réseau
      au moment d'imprimer.
   4. `print-color-adjust: exact` force l'impression des aplats de couleur
      (bandeau vert du tableau, badge de validité, ligne TOTAL TTC) même
      si l'utilisateur n'a pas coché « Graphiques d'arrière-plan ».

   🔁 EN-TÊTE RÉPÉTÉ : tout le document est encapsulé dans une table
   `.pr-doc` dont le `<thead>` (logo + DEVIS + n° + dates) est en
   `display:table-header-group` → le navigateur le redessine EN HAUT DE
   CHAQUE PAGE imprimée. Ne pas « aplatir » cette table, c'est elle qui
   porte la répétition. L'adresse de l'émetteur n'est plus dans le
   bandeau (elle ferait doublon à chaque page) : elle reste dans le bloc
   « Émetteur » de la première page.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  /* Logo horizontal transparent de la charte (bibliothèque Shopify,
     1510 × 503 px, servi à 640 px de large). URL permanente : si la
     charte change, remplacer ce fichier dans Contenu > Fichiers et
     coller la nouvelle URL ici. */
  var LOGO_URL = 'https://cdn.shopify.com/s/files/1/1044/9249/9289/files/' +
    'Logo_Bambusa_by_LPDLB_transp_97c9636c-914d-41ab-b857-3e368dc45f8b.png' +
    '?v=1784122454&width=640';

  var CLE_CACHE = 'bw-devis-logo-v1';

  function logoMemorise() {
    try { return localStorage.getItem(CLE_CACHE) || ''; } catch (e) { return ''; }
  }

  /* Mémorise le logo en data:URI : dès la 2ᵉ visite l'impression
     n'a plus besoin du réseau. Best-effort, échoue en silence. */
  function memoriserLogo(url) {
    if (logoMemorise() || !window.fetch || !window.FileReader) return;
    try {
      fetch(url, { mode: 'cors', cache: 'force-cache' })
        .then(function (r) { return r.ok ? r.blob() : null; })
        .then(function (b) {
          if (!b || !b.size || b.size > 300000) return;
          var fr = new FileReader();
          fr.onload = function () {
            try { localStorage.setItem(CLE_CACHE, String(fr.result)); } catch (e) {}
          };
          fr.readAsDataURL(b);
        })
        .catch(function () {});
    } catch (e) {}
  }

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function euro(cents) {
    return (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function dateFr(ts) {
    return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  var CSS = [
    '#bw-print{display:none}',
    '@media print{',
    '  @page{size:A4;margin:12mm 14mm 14mm}',
    '  body>*{display:none!important}',
    '  body>#bw-print{display:block!important}',
    '  #bw-print{font-family:Raleway,Helvetica,Arial,sans-serif;color:#243027;font-size:11.5px;line-height:1.45;',
    '    -webkit-print-color-adjust:exact;print-color-adjust:exact}',
    '  #bw-print *{-webkit-print-color-adjust:exact;print-color-adjust:exact}',
    '  #bw-print table.pr-doc{width:100%;border-collapse:collapse}',
    '  #bw-print table.pr-doc>thead{display:table-header-group}',
    '  #bw-print table.pr-doc>tfoot{display:table-footer-group}',
    '  #bw-print table.pr-doc>tbody{display:table-row-group}',
    '  #bw-print table.pr-doc>thead>tr>th{padding:0 0 9px;font-weight:400;text-align:left}',
    '  #bw-print table.pr-doc>tbody>tr>td{padding:0}',
    '  #bw-print table.pr-doc>tfoot>tr>td{padding:6px 0 0;font-size:8px;color:#9aa69e;text-align:right}',
    '  #bw-print .pr-head{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;',
    '    border-bottom:3px solid #246148;padding-bottom:8px}',
    '  #bw-print .pr-logo{height:58px;width:auto;max-width:230px;display:block}',
    '  #bw-print .pr-logo-txt{font-size:15px;font-weight:700;color:#246148;line-height:1.2}',
    '  #bw-print .pr-titre{text-align:right;flex:none}',
    '  #bw-print .pr-titre h1{font-size:22px;margin:0;color:#246148;letter-spacing:.02em;line-height:1}',
    '  #bw-print .pr-num{font-size:13px;font-weight:700;margin-top:2px}',
    '  #bw-print .pr-dates{font-size:10.5px;margin-top:5px;line-height:1.6}',
    '  #bw-print .pr-valid{display:inline-block;background:#d6e2b1;color:#1d4b38;padding:2px 8px;border-radius:3px;font-weight:700}',
    '  #bw-print .pr-parties{display:flex;gap:16px;margin:14px 0 4px}',
    '  #bw-print .pr-bloc{flex:1;border:1px solid #dfe4d6;border-radius:4px;padding:9px 12px}',
    '  #bw-print .pr-bloc h2{font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;color:#6c7a70;margin:0 0 5px;font-weight:700}',
    '  #bw-print .pr-bloc .pr-nom{font-weight:700;font-size:12px}',
    '  #bw-print .pr-bloc .pr-emet{font-size:10.5px;line-height:1.5}',
    '  #bw-print .pr-vide{color:#9ca3af;font-style:italic}',
    '  #bw-print .pr-projet{margin:14px 0 4px;font-size:12.5px}',
    '  #bw-print .pr-projet strong{color:#246148}',
    '  #bw-print table.pr-lignes{width:100%;border-collapse:collapse;margin:8px 0 0}',
    '  #bw-print table.pr-lignes thead th{background:#246148;color:#fff;text-align:left;padding:6px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.05em}',
    '  #bw-print table.pr-lignes td{border-bottom:1px solid #e6eade;padding:6px 8px;vertical-align:top}',
    '  #bw-print table.pr-lignes tbody tr:nth-child(even) td{background:#f8faf5}',
    '  #bw-print .pr-r{text-align:right;white-space:nowrap}',
    '  #bw-print .pr-bas{display:flex;justify-content:space-between;gap:20px;margin-top:14px;align-items:flex-start}',
    '  #bw-print .pr-note{flex:1;font-size:10.5px}',
    '  #bw-print .pr-note h2{font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;color:#6c7a70;margin:0 0 3px}',
    '  #bw-print table.pr-tot{width:52%;border-collapse:collapse;flex:none}',
    '  #bw-print table.pr-tot td{padding:4px 8px}',
    '  #bw-print table.pr-tot tr.pr-ttc td{background:#d6e2b1;color:#1d4b38;font-weight:700;font-size:13px;border-top:2px solid #246148}',
    '  #bw-print .pr-mentions{margin-top:16px;padding-top:8px;border-top:1px solid #dfe4d6;font-size:8.8px;color:#4a5850;line-height:1.5;text-align:justify}',
    '  #bw-print .pr-ident{margin-top:6px;font-size:8.5px;color:#6c7a70}',
    '  #bw-print .pr-sign{margin-top:14px;display:flex;justify-content:flex-end}',
    '  #bw-print .pr-sign div{width:46%;border:1px solid #dfe4d6;border-radius:4px;padding:8px 10px;font-size:9.5px;color:#6c7a70;min-height:58px}',
    '  #bw-print table.pr-lignes{page-break-inside:auto}',
    '  #bw-print tr{page-break-inside:avoid}',
    '  #bw-print table.pr-lignes thead{display:table-header-group}',
    '  #bw-print .pr-bas,#bw-print .pr-sign{page-break-inside:avoid}',
    '}'
  ].join('');

  var st = document.createElement('style');
  st.id = 'bw-print-styles';
  st.textContent = CSS;
  document.head.appendChild(st);

  /* Essaie une source d'image ; résout avec l'URL si elle charge en
     moins de `ms`, sinon avec une chaîne vide. Ne rejette jamais. */
  function essayer(url, ms) {
    return new Promise(function (resoudre) {
      if (!url) return resoudre('');
      var fini = false;
      var img = new Image();
      var minuteur = setTimeout(function () {
        if (!fini) { fini = true; resoudre(''); }
      }, ms || 1500);
      img.onload = function () {
        if (!fini) { fini = true; clearTimeout(minuteur); resoudre(url); }
      };
      img.onerror = function () {
        if (!fini) { fini = true; clearTimeout(minuteur); resoudre(''); }
      };
      img.src = url;
    });
  }

  /* Candidats dans l'ordre : réglage logo_devis → copie mémorisée →
     URL de la charte. Le premier qui charge gagne ; si aucun ne répond,
     l'en-tête bascule sur le nom de la société en toutes lettres. */
  function resoudreLogo(urlConf) {
    var candidats = [urlConf, logoMemorise(), LOGO_URL].filter(Boolean);
    var i = 0;
    function suivant() {
      if (i >= candidats.length) return Promise.resolve('');
      var c = candidats[i++];
      return essayer(c, c.slice(0, 5) === 'data:' ? 4000 : 1500)
        .then(function (ok) { return ok || suivant(); });
    }
    return suivant();
  }

  /* Préchauffage dès le chargement de la page. */
  (function () {
    if (logoMemorise()) return;
    var pre = new Image();
    pre.src = LOGO_URL;
    memoriserLogo(LOGO_URL);
  }());

  /* Attend le décodage de toutes les images du document imprimé.
     Sans ça, window.print() peut partir avant le logo. */
  function attendreImages(racine) {
    var imgs = Array.prototype.slice.call(racine.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();
    return Promise.all(imgs.map(function (i) {
      if (i.complete && i.naturalWidth) return Promise.resolve();
      if (i.decode) return i.decode().catch(function () {});
      return new Promise(function (r) { i.onload = i.onerror = r; });
    }));
  }

  window.BE_PRINT = function (d, t, conf, validiteJours) {
    conf = conf || {};
    var ent = conf.entreprise || {};
    var cli = conf.client || {};
    var validite = validiteJours || conf.validite || 30;
    var echeance = (d.cree || Date.now()) + validite * 24 * 60 * 60 * 1000;

    var vieux = document.getElementById('bw-print');
    if (vieux) vieux.remove();

    resoudreLogo(ent.logo).then(function (logoSrc) {
      /* ── Bandeau d'en-tête (répété en haut de chaque page) ── */
      var marque = logoSrc
        ? '<img class="pr-logo" src="' + esc(logoSrc) + '" alt="' + esc(ent.nom || 'Bambusa') + '">'
        : '<div class="pr-logo-txt">' + esc(ent.nom || '') + '</div>';

      var head = '<div class="pr-head"><div class="pr-marque">' + marque + '</div>' +
        '<div class="pr-titre"><h1>DEVIS</h1>' +
        '<div class="pr-num">' + esc(d.num || '') + '</div>' +
        '<div class="pr-dates">Émis le ' + dateFr(d.cree) + '<br>' +
        '<span class="pr-valid">Valable jusqu’au ' + dateFr(echeance) + '</span></div></div></div>';

      /* ── Parties ── */
      var lignesCli = (cli.lignes || []).filter(function (l) { return l && String(l).trim(); });
      var blocClient;
      if (cli.connecte && (cli.nom || lignesCli.length)) {
        blocClient = '<div class="pr-nom">' + esc(cli.nom || '') + '</div>' +
          '<div class="pr-emet">' +
          (cli.societe ? esc(cli.societe) + '<br>' : '') +
          lignesCli.map(function (l) { return esc(l); }).join('<br>') +
          (cli.email ? '<br>' + esc(cli.email) : '') +
          (cli.tel ? '<br>' + esc(cli.tel) : '') + '</div>';
      } else {
        blocClient = '<div class="pr-vide">Nom&nbsp;: ............................................<br>' +
          'Adresse&nbsp;: .......................................<br>' +
          '..............................................................<br>' +
          'Email / téléphone&nbsp;: ..........................</div>';
      }

      var parties = '<div class="pr-parties">' +
        '<div class="pr-bloc"><h2>Émetteur</h2><div class="pr-nom">' + esc(ent.nom || '') + '</div>' +
        '<div class="pr-emet">' +
        (ent.lignes || []).map(function (l) { return esc(l); }).join('<br>') + '</div></div>' +
        '<div class="pr-bloc"><h2>Client</h2>' + blocClient + '</div></div>';

      var projet = '<p class="pr-projet"><strong>Projet&nbsp;:</strong> ' + esc(d.nom || '') +
        (d.cp ? ' — livraison estimée pour le code postal ' + esc(d.cp) : '') + '</p>';

      /* ── Lignes ── */
      var corps = (d.lignes || []).map(function (l) {
        return '<tr><td>' + esc(l.t) + '</td>' +
          '<td class="pr-r">' + l.q + '</td>' +
          '<td class="pr-r">' + euro(l.p) + '</td>' +
          '<td class="pr-r">' + String(l.tva).replace('.', ',') + ' %</td>' +
          '<td class="pr-r">' + euro(l.p * l.q) + '</td></tr>';
      }).join('');

      var table = '<table class="pr-lignes"><thead><tr>' +
        '<th>Désignation</th><th class="pr-r">Qté</th><th class="pr-r">P.U. TTC</th>' +
        '<th class="pr-r">TVA</th><th class="pr-r">Total TTC</th></tr></thead><tbody>' +
        corps + '</tbody></table>';

      /* ── Totaux ── */
      var tot = '<table class="pr-tot"><tbody>' +
        '<tr><td>Total HT</td><td class="pr-r">' + euro(t.ht) + '</td></tr>' +
        (t.tva55 > 0.5 ? '<tr><td>TVA 5,5 %</td><td class="pr-r">' + euro(t.tva55) + '</td></tr>' : '') +
        (t.tva10 > 0.5 ? '<tr><td>TVA 10 %</td><td class="pr-r">' + euro(t.tva10) + '</td></tr>' : '') +
        (t.tva20 > 0.5 ? '<tr><td>TVA 20 %</td><td class="pr-r">' + euro(t.tva20) + '</td></tr>' : '') +
        '<tr><td>Frais de port estimés' + (t.kg ? ' (' + t.kg.toFixed(1).replace('.', ',') + ' kg)' : '') +
        '</td><td class="pr-r">' + (t.port === 0 ? 'offerts' : euro(t.port)) + '</td></tr>' +
        '<tr class="pr-ttc"><td>TOTAL TTC</td><td class="pr-r">' + euro(t.ttc) + '</td></tr>' +
        '</tbody></table>';

      var bas = '<div class="pr-bas"><div class="pr-note">' +
        (d.note ? '<h2>Note du client</h2>' + esc(d.note) : '') +
        '</div>' + tot + '</div>';

      var signature = '<div class="pr-sign"><div>Bon pour accord — date et signature du client&nbsp;:</div></div>';

      var pied = '<div class="pr-mentions">' + ((conf.mentions || {}).pied || '') +
        '<div class="pr-ident">' + (ent.identifiants || []).map(function (l) { return esc(l); }).join(' · ') + '</div></div>';

      var contenu = parties + projet + table + bas + signature + pied;

      /* Table porteuse : thead = bandeau répété sur chaque page,
         tfoot = rappel discret du n° de devis en pied de chaque page. */
      var doc = '<table class="pr-doc">' +
        '<thead><tr><th>' + head + '</th></tr></thead>' +
        '<tfoot><tr><td>' + esc(ent.nom || '') + ' — Devis ' + esc(d.num || '') + '</td></tr></tfoot>' +
        '<tbody><tr><td>' + contenu + '</td></tr></tbody>' +
        '</table>';

      var el = document.createElement('div');
      el.id = 'bw-print';
      el.innerHTML = doc;
      document.body.appendChild(el);

      /* Le navigateur nomme le PDF d'après le titre du document. */
      var titreOrigine = document.title;
      document.title = 'Devis ' + (d.num || '') + ' — Bambusa';

      function restaurer() {
        document.title = titreOrigine;
        window.removeEventListener('afterprint', restaurer);
      }
      window.addEventListener('afterprint', restaurer);
      setTimeout(restaurer, 8000);

      /* On n'imprime qu'une fois le logo réellement décodé.
         ⚠️ setTimeout et NON requestAnimationFrame : rAF ne se déclenche
         pas dans un onglet en arrière-plan → l'impression ne partirait
         jamais (vérifié en vrai le 04/08). */
      attendreImages(el).then(function () {
        setTimeout(function () { window.print(); }, 60);
      });
    });
  };
}());
